import React from 'react';
import { useTranslation } from 'react-i18next';
import './WeeklyAdviceCard.css';

function WeeklyAdviceCard({ weekInfo }) {
  const { t } = useTranslation();
  
  if (!weekInfo) {
    return (
      <div className="weekly-advice-card empty-state">
        <div className="empty-state-decoration">
          <span className="floating-heart">💕</span>
          <span className="floating-star">✨</span>
          <span className="floating-heart-2">💖</span>
        </div>
        <div className="empty-state-content">
          <div className="empty-state-icon-wrapper">
            <div className="empty-state-icon">🤰</div>
            <div className="icon-glow"></div>
          </div>
          <h3 className="empty-state-title">
            <span className="title-emoji">🌟</span>
            Your Baby's Journey Awaits
            <span className="title-emoji">🌟</span>
          </h3>
          <p className="empty-state-welcome">
            We're here to guide you through every beautiful moment of your pregnancy journey! 💝
          </p>
          <p className="empty-state-description">
            Once your pregnancy profile is set up, you'll discover amazing insights about your little one's growth:
          </p>
          <div className="empty-state-features">
            <div className="feature-item">
              <span className="feature-emoji">👶</span>
              <div className="feature-content">
                <strong>Baby Development</strong>
                <span>Watch your little one grow - size, weight, and precious milestones</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-emoji">👩</span>
              <div className="feature-content">
                <strong>Your Beautiful Changes</strong>
                <span>Understanding your body's amazing transformation</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-emoji">🥗</span>
              <div className="feature-content">
                <strong>Nourishing Nutrition</strong>
                <span>Delicious foods that support you and your baby</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-emoji">🏃</span>
              <div className="feature-content">
                <strong>Gentle Activities</strong>
                <span>Safe exercises to keep you feeling great</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-emoji">🏥</span>
              <div className="feature-content">
                <strong>Care Reminders</strong>
                <span>Important appointments and tests coming up</span>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-emoji">💖</span>
              <div className="feature-content">
                <strong>Weekly Checklist</strong>
                <span>Helpful tasks to make each week special</span>
              </div>
            </div>
          </div>
          <div className="empty-state-encouragement">
            <span className="encouragement-icon">💝</span>
            <p>
              Set up your pregnancy profile to unlock personalized weekly insights tailored just for you and your baby!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const baby = weekInfo.baby || {};
  const mother = weekInfo.mother || {};
  const advice = weekInfo.advice || {};
  const nutrition = advice.nutrition || {};
  const activity = advice.activity || {};
  const health = advice.health || {};
  const checklist = weekInfo.checklist || [];
  const appointments = weekInfo.appointments || [];

  return (
    <div className="weekly-advice-card">
      <div className="advice-header">
        <h2>
          📅 {t('pregnancy.week', 'Week')} {weekInfo.week || weekInfo.currentWeek}
          {weekInfo.currentDay !== undefined && `, Day ${weekInfo.currentDay}`}
        </h2>
        <div className="trimester-badge">
          {weekInfo.trimester === 1 && '1️⃣'}
          {weekInfo.trimester === 2 && '2️⃣'}
          {weekInfo.trimester === 3 && '3️⃣'}
          {' '}
          {t(`pregnancy.trimester.${weekInfo.trimester}`, `Trimester ${weekInfo.trimester}`)}
        </div>
      </div>

      <div className="advice-content">
        {/* Baby Development */}
        <div className="advice-section">
          <h3>👶 {t('pregnancy.baby.development', 'Baby Development')}</h3>
          <div className="baby-info">
            <div className="info-item">
              <span className="info-label">📏 {t('pregnancy.size', 'Size')}:</span>
              <span className="info-value">{baby.size || 'N/A'}</span>
            </div>
            {baby.weight && (
              <div className="info-item">
                <span className="info-label">⚖️ {t('pregnancy.weight', 'Weight')}:</span>
                <span className="info-value">{baby.weight}</span>
              </div>
            )}
            <div className="info-description">
              {baby.development || 'Baby is growing and developing.'}
            </div>
            {baby.milestones && baby.milestones.length > 0 && (
              <div className="milestones">
                <strong>{t('pregnancy.milestones', 'Milestones')}:</strong>
                <ul>
                  {baby.milestones.map((milestone, idx) => (
                    <li key={idx}>✨ {milestone}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Mother's Changes */}
        <div className="advice-section">
          <h3>👩 {t('pregnancy.mother.changes', "Mother's Changes")}</h3>
          <div className="mother-info">
            <div className="info-description">
              {mother.physicalChanges || 'Your body continues to change.'}
            </div>
            {mother.symptoms && mother.symptoms.length > 0 && (
              <div className="symptoms">
                <strong>{t('pregnancy.symptoms', 'Symptoms')}:</strong>
                <ul>
                  {mother.symptoms.map((symptom, idx) => (
                    <li key={idx}>💡 {symptom}</li>
                  ))}
                </ul>
              </div>
            )}
            {mother.energyLevel && (
              <div className="info-item">
                <span className="info-label">⚡ {t('pregnancy.energy', 'Energy Level')}:</span>
                <span className="info-value">{mother.energyLevel}</span>
              </div>
            )}
          </div>
        </div>

        {/* Nutrition Advice */}
        {nutrition.focus && (
          <div className="advice-section">
            <h3>🥗 {t('pregnancy.nutrition', 'Nutrition')}</h3>
            <div className="nutrition-advice">
              <div className="focus-foods">
                <strong>✨ {t('pregnancy.focus.on', 'Focus On')}:</strong>
                <ul>
                  {nutrition.focus.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              {nutrition.avoid && nutrition.avoid.length > 0 && (
                <div className="avoid-foods">
                  <strong>❌ {t('pregnancy.avoid', 'Avoid')}:</strong>
                  <ul>
                    {nutrition.avoid.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Advice */}
        {activity.recommended && (
          <div className="advice-section">
            <h3>🏃 {t('pregnancy.activity', 'Activity')}</h3>
            <div className="activity-advice">
              <div className="recommended-activities">
                <strong>✨ {t('pregnancy.recommended', 'Recommended')}:</strong>
                <ul>
                  {activity.recommended.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              {activity.avoid && activity.avoid.length > 0 && (
                <div className="avoid-activities">
                  <strong>❌ {t('pregnancy.avoid', 'Avoid')}:</strong>
                  <ul>
                    {activity.avoid.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Health & Appointments */}
        {(appointments.length > 0 || (health.appointments && health.appointments.length > 0) || (health.tests && health.tests.length > 0)) && (
          <div className="advice-section">
            <h3>🏥 {t('pregnancy.appointments', 'Appointments & Tests')}</h3>
            <div className="appointments-list">
              {appointments.map((appt, idx) => (
                <div key={idx} className="appointment-item">
                  {appt.type === 'appointment' ? '📅' : '🧪'} {appt.name}
                </div>
              ))}
              {health.appointments && health.appointments.map((appt, idx) => (
                <div key={`appt-${idx}`} className="appointment-item">
                  📅 {appt}
                </div>
              ))}
              {health.tests && health.tests.map((test, idx) => (
                <div key={`test-${idx}`} className="appointment-item">
                  🧪 {test}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {health.warnings && health.warnings.length > 0 && (
          <div className="advice-section warning-section">
            <h3>⚠️ {t('pregnancy.warnings', 'Important Warnings')}</h3>
            <ul>
              {health.warnings.map((warning, idx) => (
                <li key={idx}>⚠️ {warning}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Mental Health */}
        {advice.mentalHealth && (
          <div className="advice-section">
            <h3>🧘 {t('pregnancy.mental.health', 'Mental Health')}</h3>
            <p>{advice.mentalHealth}</p>
          </div>
        )}

        {/* Sleep Tips */}
        {advice.sleep && (
          <div className="advice-section">
            <h3>😴 {t('pregnancy.sleep', 'Sleep Tips')}</h3>
            <p>{advice.sleep}</p>
          </div>
        )}

        {/* Hydration */}
        {advice.hydration && (
          <div className="advice-section">
            <h3>💧 {t('pregnancy.hydration', 'Hydration')}</h3>
            <p>{advice.hydration}</p>
          </div>
        )}

        {/* Weekly Checklist */}
        {checklist.length > 0 && (
          <div className="advice-section checklist-section">
            <h3>💖 {t('pregnancy.weekly.checklist', 'This Week\'s Checklist')}</h3>
            <ul className="checklist-items">
              {checklist.map((item, idx) => (
                <li key={idx}>
                  <input type="checkbox" id={`check-${idx}`} />
                  <label htmlFor={`check-${idx}`}>{item}</label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default WeeklyAdviceCard;

